import {ReactNode, createContext, useContext, useMemo, useState, useCallback} from "react";
import Cookies from "js-cookie";
import {User} from "../models";
import {getInfos} from "../services/company";
import {COOKIES_COMPANY_ID, DOMAIN_NAME as domain} from "../helper/constants";


interface AuthState {
    user: User | null;
    status: "loading" | "authenticated" | "unauthenticated";
}
interface AuthContextValue extends AuthState {
    fetchUser: () => Promise<void>
    logout: () => void
    pendingIdentity: boolean
    verified: boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider(props: {children: ReactNode}) {
    const [user, setUser] = useState<User | null>(null);
    const [status, setStatus] = useState<AuthState["status"]>("loading");
    const pendingIdentity = useMemo(function () {
        return user?.verificationStatus === "pending";
    }, [user]);
    const verified = useMemo(function () {
        return user?.verificationStatus === "verified";
    }, [user]);
    const fetchUser = useCallback(async function () {
        let res: User;
        try {
            setStatus("loading");
            res = await getInfos();
            if (res) {
                setUser(res);
                Cookies.set(COOKIES_COMPANY_ID, `${res.CompanyId}`, {domain});
                setStatus("authenticated");
            } else {
                setStatus("unauthenticated");
            }
        } catch (err) {
            console.error("Error fetch user infos: ", err);
            setUser(null);
            setStatus("unauthenticated");
        }
    }, []);
    const logout = useCallback(function () {
        setUser(null);
        setStatus("unauthenticated");
    }, []);
    return <AuthContext.Provider value={{
        fetchUser,
        logout,
        pendingIdentity,
        status,
        user,
        verified
    }}>
        {props.children}
    </AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return ctx;
}
